// Legacy: pro-frontend/src/app/manage-portfolio/newpreview-portfolio/newpreview-portfolio.component.ts
// + public portfolio language formatting from portfolio.component.ts

import { formatLanguageEntryForDisplay } from '@/features/portfoliosettings/lib/language-display';

import type {
  PortfolioCategory,
  PortfolioCategoryEntry,
  PortfolioRenderData,
  PortfolioResume,
} from '../types';
import { resolvePortfolioTemplateImageUrl } from './portfolio-template-image';

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function formatDateRange(entry: PortfolioCategoryEntry): string {
  const startMonth = entry.start_date_month;
  const startYear = entry.start_date_year;
  const endMonth = entry.end_date_month;
  const endYear = entry.end_date_year;

  const formatPart = (month: number | null | undefined, year: number | null | undefined) => {
    if (!month && !year) return '';
    if (month && month >= 1 && month <= 12 && year) {
      return `${MONTH_NAMES[month - 1]} ${year}`;
    }
    if (month && month >= 1 && month <= 12) return MONTH_NAMES[month - 1];
    return year ? String(year) : '';
  };

  const startText = formatPart(startMonth, startYear);
  const endText = entry.is_current ? 'Present' : formatPart(endMonth, endYear);
  if (startText && endText) return `${startText} - ${endText}`;
  return startText || endText || '';
}

function getSkills(resume: PortfolioResume): string[] {
  const skillCat = (resume.categories || []).find((c) => c.category_type === 'skill');
  if (skillCat && skillCat.entries) {
    return skillCat.entries.map((e) => String(e.title || e.display_title || ''));
  }
  return [];
}

function getTools(resume: PortfolioResume): Array<{ category: string; items: string }> {
  const skillCat = (resume.categories || []).find((c) => c.category_type === 'skill');
  if (skillCat && skillCat.entries) {
    return skillCat.entries.map((e) => ({
      category: String(e.title || e.display_title || ''),
      items: String(e.description || e.display_description || ''),
    }));
  }
  return [];
}

function getExperience(resume: PortfolioResume) {
  const workCat = (resume.categories || []).find((c) => c.category_type === 'work');
  if (workCat && workCat.entries) {
    return workCat.entries.map((e) => ({
      company: String(e.work_company || ''),
      role: String(e.title || e.display_title || ''),
      period: formatDateRange(e),
      description: String(e.description || e.display_description || ''),
    }));
  }
  return [];
}

function getAchievements(resume: PortfolioResume) {
  if (resume.highlights && resume.highlights.length > 0) {
    return resume.highlights.map((h) => ({
      title: String(h.title || h.display_title || ''),
      description: String(h.description || h.display_description || ''),
      image: String(
        (h as { image_url?: string }).image_url ||
          (h.ResumeEntryFile && h.ResumeEntryFile[0] && h.ResumeEntryFile[0].file_url) ||
          '',
      ),
    }));
  }
  return [];
}

function entryBaseTitle(entry: PortfolioCategoryEntry): string {
  return String(
    entry.display_title ||
      entry.title ||
      (entry as { education_title?: string }).education_title ||
      (entry as { skill_name?: string }).skill_name ||
      (entry as { school_name?: string }).school_name ||
      (entry as { company_name?: string }).company_name ||
      (entry as { project_name?: string }).project_name ||
      (entry as { certification_name?: string }).certification_name ||
      '',
  );
}

function normalizeCategories(categories: PortfolioCategory[]): PortfolioCategory[] {
  if (!categories) return [];
  return categories.map((cat) => {
    let entries =
      cat.entries ||
      (cat.ResumeEntry as PortfolioCategoryEntry[] | undefined) ||
      (cat.ResumeSkill as PortfolioCategoryEntry[] | undefined) ||
      (cat.ResumeEducation as PortfolioCategoryEntry[] | undefined) ||
      (cat.ResumeWork as PortfolioCategoryEntry[] | undefined) ||
      (cat.ResumeAcademicProject as PortfolioCategoryEntry[] | undefined) ||
      (cat.ResumeCertification as PortfolioCategoryEntry[] | undefined) ||
      (cat.ResumeRecognition as PortfolioCategoryEntry[] | undefined) ||
      (cat.ResumeVolunteering as PortfolioCategoryEntry[] | undefined) ||
      (cat.ResumeMisc as PortfolioCategoryEntry[] | undefined) ||
      (cat.ResumeLanguage as PortfolioCategoryEntry[] | undefined) ||
      (cat.ResumeInterest as PortfolioCategoryEntry[] | undefined) ||
      (cat.ResumeAvailability as PortfolioCategoryEntry[] | undefined) ||
      (cat.ResumeAward as PortfolioCategoryEntry[] | undefined) ||
      (cat.ResumePublication as PortfolioCategoryEntry[] | undefined) ||
      (cat.ResumeTestScore as PortfolioCategoryEntry[] | undefined) ||
      (cat.ResumeWorkshop as PortfolioCategoryEntry[] | undefined) ||
      (cat.ResumeReference as PortfolioCategoryEntry[] | undefined) ||
      [];

    if (entries.length === 0) {
      const possibleKey = Object.keys(cat).find(
        (key) => Array.isArray(cat[key]) && key.startsWith('Resume'),
      );
      if (possibleKey) {
        entries = cat[possibleKey] as PortfolioCategoryEntry[];
      }
    }

    const isLanguage = cat.category_type === 'language';
    const languageMode = cat.language_display_mode || 'grid';

    return {
      ...cat,
      entries: entries.map((entry) => {
        if (isLanguage) {
          const langResult = formatLanguageEntryForDisplay(
            {
              title: entry.title || entryBaseTitle(entry),
              listening_level: entry.listening_level as string | null | undefined,
              reading_level: entry.reading_level as string | null | undefined,
              spoken_interaction_level: entry.spoken_interaction_level as
                | string
                | null
                | undefined,
              spoken_production_level: entry.spoken_production_level as string | null | undefined,
              writing_level: entry.writing_level as string | null | undefined,
              proficiency_level: entry.proficiency_level as string | null | undefined,
              normal_level: entry.normal_level as string | null | undefined,
            },
            languageMode,
          );
          return {
            ...entry,
            display_title: langResult.title,
            display_description: langResult.description,
          };
        }

        const title = entryBaseTitle(entry);
        const description = entry.display_description || entry.description || '';
        return {
          ...entry,
          display_title: String(title),
          display_description: String(description),
        };
      }),
    };
  });
}

function normalizeHighlights(
  highlights: PortfolioCategoryEntry[] | undefined,
  categories: PortfolioCategory[],
): PortfolioCategoryEntry[] {
  if (!highlights || highlights.length === 0) {
    return [];
  }
  return highlights.map((highlight) => {
    const category = highlight.category as
      | { id?: number | string; category_type?: string }
      | undefined;
    if (category && category.category_type === 'language') {
      const parentCat = categories.find((c) => String(c.id) === String(category.id));
      const mode = (parentCat && parentCat.language_display_mode) || 'grid';
      const langResult = formatLanguageEntryForDisplay(
        {
          title: highlight.title || entryBaseTitle(highlight),
          listening_level: highlight.listening_level as string | null | undefined,
          reading_level: highlight.reading_level as string | null | undefined,
          spoken_interaction_level: highlight.spoken_interaction_level as
            | string
            | null
            | undefined,
          spoken_production_level: highlight.spoken_production_level as string | null | undefined,
          writing_level: highlight.writing_level as string | null | undefined,
          proficiency_level: highlight.proficiency_level as string | null | undefined,
          normal_level: highlight.normal_level as string | null | undefined,
        },
        mode,
      );
      return {
        ...highlight,
        display_title: langResult.title,
        display_description: langResult.description,
      };
    }
    return {
      ...highlight,
      display_title: entryBaseTitle(highlight),
      display_description: String(highlight.display_description || highlight.description || ''),
    };
  });
}

function summaryText(resume: PortfolioResume): string {
  if (!resume.summary) return '';
  if (typeof resume.summary === 'string') return resume.summary;
  return resume.summary.description || '';
}

/** Legacy: designation || (headline when not hidden) || preferred_job */
export function resolvePublicDesignation(resume: PortfolioResume): string {
  if (resume.designation && String(resume.designation).trim()) {
    return String(resume.designation).trim();
  }
  if (resume.is_headline_hidden) {
    return '';
  }
  const headline = resume.headline || resume.preferred_job || resume.preferredJob;
  return headline ? String(headline).trim() : '';
}

export function mapResumeToPortfolioData(resume: PortfolioResume): PortfolioRenderData {
  const categories = normalizeCategories(resume.categories || []);
  const highlights = normalizeHighlights(resume.highlights || [], categories);
  const summary = summaryText(resume);
  const designation = resolvePublicDesignation(resume);

  return {
    name: resume.name ? String(resume.name) : '',
    designation,
    shortBio: summary,
    company: resume.company || '',
    role_highlight: resume.designation || '',
    summary,
    profile_image: resume.image_url || '',
    is_headline_hidden: Boolean(resume.is_headline_hidden),
    stats: [
      { count: '0', label: 'YEARS OF EXPERIENCE' },
      { count: '0', label: 'PROJECTS COMPLETED' },
      { count: '0', label: 'WORLDWIDE CLIENTS' },
    ],
    social: {
      twitter: resume.twitter_url || '',
      linkedin: resume.linkedin_url || '',
      instagram: resume.instagram_url || '',
      website: resume.personal_website_url || '',
    },
    twitter_url: resume.twitter_url,
    is_twitter_url_hidden: resume.is_twitter_url_hidden,
    instagram_url: resume.instagram_url,
    is_instagram_url_hidden: resume.is_instagram_url_hidden,
    facebook_url: resume.facebook_url,
    is_facebook_url_hidden: resume.is_facebook_url_hidden,
    linkedin_url: resume.linkedin_url,
    is_linkedin_url_hidden: resume.is_linkedin_url_hidden,
    personal_website_url: resume.personal_website_url,
    is_personal_website_url_hidden: resume.is_personal_website_url_hidden,
    youtube_url: resume.youtube_url,
    is_youtube_url_hidden: resume.is_youtube_url_hidden,
    vimeo_url: resume.vimeo_url,
    is_vimeo_url_hidden: resume.is_vimeo_url_hidden,
    dribble_url: resume.dribble_url,
    is_dribble_url_hidden: resume.is_dribble_url_hidden,
    github_url: resume.github_url,
    is_github_url_hidden: resume.is_github_url_hidden,
    carbonmade_url: resume.carbonmade_url,
    is_carbonmade_url_hidden: resume.is_carbonmade_url_hidden,
    pinterest_url: resume.pinterest_url,
    is_pinterest_url_hidden: resume.is_pinterest_url_hidden,
    soundcloud_url: resume.soundcloud_url,
    is_soundcloud_url_hidden: resume.is_soundcloud_url_hidden,
    tumblr_url: resume.tumblr_url,
    is_tumblr_url_hidden: resume.is_tumblr_url_hidden,
    portfolio_url: resume.portfolio_url,
    is_portfolio_url_hidden: resume.is_portfolio_url_hidden,
    skills: getSkills({ ...resume, categories }),
    tools: getTools({ ...resume, categories }),
    experience: getExperience({ ...resume, categories }),
    achievements: getAchievements({ ...resume, highlights }),
    summaryTitle: 'Summary',
    summaryStatus: 1,
    highlightsStatus: 1,
    attachmentStatus: 1,
    descriptionStatus: 1,
    categories,
    hightlights: highlights,
  };
}

/** Public view: drop hidden entries and empty categories. */
export function filterHiddenForPublic(resume: PortfolioResume): PortfolioResume {
  const categories = (resume.categories || [])
    .map((cat) => {
      const entries = (cat.entries || []).filter((e) => e.is_hidden !== true);
      return { ...cat, entries };
    })
    .filter((cat) => (cat.entries || []).length > 0);

  return { ...resume, categories };
}

export function unwrapResume(
  resume: PortfolioResume | PortfolioResume[] | undefined,
): PortfolioResume | null {
  if (!resume) return null;
  if (Array.isArray(resume)) {
    return resume[0] || null;
  }
  return resume;
}

export function getPortfolioImage(portfolio: {
  template?: {
    image?: string;
    preview_image_url?: string;
    previewImageUrl?: string;
  } | null;
  preview_image_url?: string | null;
  previewImageUrl?: string | null;
}): string {
  const template = portfolio.template || {};
  return resolvePortfolioTemplateImageUrl({
    candidates: [
      template.image,
      template.preview_image_url,
      template.previewImageUrl,
      portfolio.preview_image_url,
      portfolio.previewImageUrl,
    ],
  });
}
